<template>
  <v-dialog
      v-model="dialog"
      max-width="700"
      persistent
      scrollable
  >
    <v-card>
      <v-card-title>
        {{ isNew ? $t('schematrons.newSchematron') : $t('schematrons.editSchematron') }}
      </v-card-title>

      <v-card-text>
        <v-alert
            v-if="error"
            type="error"
            dense
            text
        >
          {{ error }}
        </v-alert>

        <v-form>
          <v-text-field
              v-model="form.name"
              v-validate="{ required: true, max: 255 }"
              label="Name"
              name="name"
              maxlength="255"
              :error-messages="errors.collect('name')"
          />

          <v-select
              v-model="form.scope"
              v-validate="{ required: true }"
              :items="scopeItems"
              label="Scope"
              name="scope"
              :error-messages="errors.collect('scope')"
              :hint="scopeHint"
              persistent-hint
          />

          <!-- Distinct keys: without them Vue patches one autocomplete into the other
               when the scope changes, and VeeValidate keeps validating the stale
               field - a hidden always-failing "required" that mutes the Save button. -->
          <v-autocomplete
              v-if="needsState"
              key="state"
              v-model="form.state"
              v-validate="{ required: true }"
              :items="stateItems"
              item-text="title"
              item-value="stateID"
              label="State"
              name="state"
              :error-messages="errors.collect('state')"
          />

          <v-autocomplete
              v-if="isClientScope"
              key="clientId"
              v-model="form.clientId"
              v-validate="{ required: true }"
              :items="clientItems"
              item-text="title"
              item-value="registryId"
              label="Client"
              name="clientId"
              data-vv-as="Client"
              :error-messages="errors.collect('clientId')"
          />

          <v-row>
            <v-col cols="6">
              <v-select
                  v-model="form.datasetType"
                  :items="datasetItems"
                  label="Dataset"
                  name="datasetType"
                  :disabled="fieldsLocked"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                  v-model="form.nemsisVersion"
                  v-validate="{ required: true }"
                  :items="supportedVersions"
                  label="NEMSIS version"
                  name="nemsisVersion"
                  data-vv-as="NEMSIS version"
                  :disabled="fieldsLocked"
                  :error-messages="errors.collect('nemsisVersion')"
              />
            </v-col>
          </v-row>
          <p
              v-if="fieldsLocked"
              class="caption grey--text text--darken-1 mt-n2"
          >
            {{ $t('schematrons.fieldsLocked') }}
          </p>

          <v-textarea
              v-model="form.description"
              label="Description"
              name="description"
              rows="2"
              maxlength="1000"
          />

          <!-- Only national and state rulesets have a say: a client one names its single
               recipient on the card, so Active is already the switch, and a county one has
               no audience that could be worked out from the client. -->
          <v-switch
              v-if="showAutoAssign"
              v-model="form.autoAssign"
              label="Assign automatically"
              :hint="scopeHint"
              persistent-hint
          />

          <v-switch
              v-model="form.isActive"
              label="Active"
          />

          <template v-if="isNew">
            <v-divider class="my-4" />

            <v-file-input
                v-model="file"
                v-validate="{ required: true }"
                label="Schematron file (.sch)"
                name="file"
                data-vv-as="schematron file"
                accept=".sch,.xml"
                prepend-icon=""
                prepend-inner-icon="description"
                :error-messages="errors.collect('file')"
                show-size
            />

            <date-picker
                :date.sync="form.releasedAt"
                label="Released at"
            />

            <v-text-field
                v-model="form.notes"
                label="Notes"
                name="notes"
                maxlength="500"
                hint="Why this version was uploaded and what changed"
            />
          </template>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <p
            v-if="saving && isNew"
            class="caption grey--text text--darken-1 mb-0 ml-2"
        >
          {{ $t('schematrons.compiling') }}
        </p>
        <v-spacer />
        <v-btn
            text
            :disabled="saving"
            @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
            color="primary"
            :loading="saving"
            @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import schematronsApi from '@/api/schematrons';
import clientsApi from '@/api/clients';
import fetchAllStates from '@/api/states';
import DatePicker from '@/components/DatePicker';
import SCHEMATRON_SCOPES from '@/enums/schematronScopes';
import NEMSIS_DATASETS from '@/enums/nemsisDatasets';

export default {
  name: 'EditSchematronDialog',
  components: { DatePicker },

  data() {
    return {
      dialog: false,
      saving: false,
      error: null,
      schematron: null,
      file: null,
      states: [],
      clients: [],
      supportedVersions: [],
      currentVersion: null,
      form: this.emptyForm(),
      datasetItems: Object.values(NEMSIS_DATASETS),
      scopeItems: Object.values(SCHEMATRON_SCOPES).map(value => ({
        value,
        text: value.charAt(0).toUpperCase() + value.slice(1),
      })),
    };
  },

  computed: {
    isNew() {
      return !this.schematron;
    },
    // Only the card is editable afterwards: a .sch is written against one dataset and one
    // NEMSIS version, and changing either would leave the rules matching nothing.
    fieldsLocked() {
      return Boolean(this.schematron && this.schematron.fieldsLocked);
    },
    needsState() {
      return [SCHEMATRON_SCOPES.STATE, SCHEMATRON_SCOPES.COUNTY].includes(this.form.scope);
    },
    isClientScope() {
      return this.form.scope === SCHEMATRON_SCOPES.CLIENT;
    },
    showAutoAssign() {
      return [SCHEMATRON_SCOPES.NATIONAL, SCHEMATRON_SCOPES.STATE].includes(this.form.scope);
    },
    scopeHint() {
      return this.$t(`schematrons.autoAssignHint.${this.form.scope}`);
    },
    stateItems() {
      return this.states.map(item => ({
        title: `${item.stateID} - ${item.state}`,
        stateID: item.stateID,
      }));
    },
    clientItems() {
      // registryId, not registryID - that is what the clients endpoint returns.
      return this.clients.map(item => ({
        title: item.name,
        registryId: item.registryId,
      }));
    },
  },

  methods: {
    emptyForm() {
      return {
        name: null,
        scope: SCHEMATRON_SCOPES.NATIONAL,
        datasetType: NEMSIS_DATASETS.EMS,
        nemsisVersion: this.currentVersion || null,
        state: null,
        clientId: null,
        description: null,
        autoAssign: true,
        isActive: true,
        releasedAt: null,
        notes: null,
      };
    },

    async open(schematron = null) {
      this.error = null;
      this.file = null;
      this.schematron = schematron;
      this.form = schematron
        ? {
          ...this.emptyForm(),
          name: schematron.name,
          scope: schematron.scope,
          datasetType: schematron.datasetType,
          nemsisVersion: schematron.nemsisVersion,
          state: schematron.state,
          clientId: schematron.clientId,
          description: schematron.description,
          autoAssign: schematron.autoAssign,
          isActive: schematron.isActive,
        }
        : this.emptyForm();

      this.dialog = true;
      await this.$validator.reset();
      await this.loadReferences();
    },

    // Both endpoints answer in Dingo's collection shape, { results: [...] }.
    async loadReferences() {
      if (!this.supportedVersions.length) {
        const meta = await schematronsApi.getMeta();
        this.supportedVersions = meta.supportedVersions;
        this.currentVersion = meta.version;
        if (this.isNew && !this.form.nemsisVersion) {
          this.form.nemsisVersion = meta.version;
        }
      }
      if (!this.states.length) {
        const states = await fetchAllStates();
        this.states = states.results;
      }
      if (!this.clients.length) {
        const clients = await clientsApi.fetchList({});
        this.clients = clients.results;
      }
    },

    close() {
      this.dialog = false;
      this.schematron = null;
      this.file = null;
    },

    cardFields() {
      return {
        name: this.form.name,
        scope: this.form.scope,
        datasetType: this.form.datasetType,
        nemsisVersion: this.form.nemsisVersion,
        state: this.needsState ? this.form.state : null,
        clientId: this.isClientScope ? this.form.clientId : null,
        description: this.form.description,
        autoAssign: this.form.autoAssign,
        isActive: this.form.isActive,
      };
    },

    /**
     * The dialog body scrolls while the buttons stay put, so a field failing validation
     * can easily be off screen when Save is pressed - highlighting it alone looks like
     * nothing happened.
     */
    scrollToFirstError() {
      const failed = this.$validator.errors.items[0];
      if (!failed) {
        return;
      }

      const field = this.$el.querySelector(`[name="${failed.field}"]`);
      if (field) {
        field.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    },

    async save() {
      if (!await this.$validator.validateAll()) {
        this.scrollToFirstError();
        return;
      }

      this.error = null;
      this.saving = true;

      try {
        if (this.isNew) {
          await schematronsApi.createSchematron(
            {
              ...this.cardFields(),
              releasedAt: this.form.releasedAt || new Date().toISOString().substring(0, 10),
              notes: this.form.notes,
            },
            this.file,
          );
        } else {
          await schematronsApi.updateSchematron(this.schematron.id, this.cardFields());
        }

        this.$emit('saved');
        this.close();
      } catch (e) {
        // The backend answers 400 with a message the admin can act on - an unparsable
        // file, rules Saxon refuses, a scope with nothing to match.
        this.error = (e.response && e.response.data && e.response.data.message)
          || 'Could not save the schematron.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
